


































































































































































import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Magazine from '@/models/Magazine';
import User from '@/models/User';
import Location from '@/models/Location';
import EventBus from '@/misc/EventBus';
import Bookmark, { BookmarkType } from '@/models/Bookmark';
import { mixins } from 'vue-class-component';
import ScrollMixin, { ScrollDirection } from '@/misc/ScrollMixin';
import moment from 'moment';

enum SlideValue {
    LEFT = 'slide-left',
    RIGHT = 'slide-right',
}

const MagazineStore = namespace('magazine');
const TrackingStore = namespace('tracking');
const AuthStore = namespace('auth');
const BookmarkStore = namespace('bookmark');
const ApplicationStore = namespace('application');

@Component({
    components: {
        PageComponent: () => import(
            /* webpackChunkName: "PageComponent" */
            '@/components/reader/Page.component.vue'),
        ThumbnailComponent: () => import(
            /* webpackChunkName: "ThumbnailComponent" */
            '@/components/reader/Thumbnail.component.vue'),
        AccessibilityButtonComponent: () => import(
            /* webpackChunkName: "AccessibilityButtonComponent" */
            '@/components/shared/AccessibilityButton.component.vue'),
        ZoomButtonComponent: () => import(
            /* webpackChunkName: "ZoomButtonComponent" */
            '@/components/reader/ZoomButton.component.vue')
    }
})
export default class ReaderComponent extends mixins(ScrollMixin) {

    public BookmarkType = BookmarkType;

    @AuthStore.Getter('currentUser')
    private currentUser!: User;

    @AuthStore.Getter('currentLocation')
    private currentLocation!: Location;

    @MagazineStore.Getter('selectedMagazine')
    private selectedMagazine!: Magazine;

    @TrackingStore.Mutation('startTracking')
    private startTracking!: (payload: {
        locationId: string,
        publicationId: string,
        magazineId: string,
        readerId: string,
        publisherId: string
    }) => void;

    @TrackingStore.Mutation('addTrackingPage')
    private addTrackingPage!: (payload: { page: string, splitMode: boolean }) => void;

    @TrackingStore.Action('sendTracking')
    private sendTracking!: (isOfflineData?: boolean) => Promise<void>;

    @TrackingStore.Mutation('clearTracking')
    private clearTracking!: () => void;

    @BookmarkStore.Mutation('saveBookmark')
    private saveBookmark!: (bookmark: Bookmark) => void;

    @BookmarkStore.Mutation('removeBookmark')
    private removeBookmark!: (bookmark: Bookmark) => void;

    @BookmarkStore.Getter('activeBookmark')
    private activeBookmark?: Bookmark;

    @ApplicationStore.Getter('accessibilityControls')
    private _accessibilityControls!: boolean;

    @ApplicationStore.Getter('noAnimations')
    private noAnimations!: boolean;

    @ApplicationStore.Action('setInactivityTimer')
    private setInactivityTimer!: () => void;

    @ApplicationStore.Action('clearInactivityTimer')
    private clearInactivityTimer!: () => void;

    get accessibilityControls(): boolean {
        return this._accessibilityControls;
    }

    /**
     * Show one or two pages on the screen
     */
    public isSplitMode?: boolean;

    public currentPage: number = 0;
    public changedPage: boolean = false;
    public slideMode: string = SlideValue.LEFT;
    public showLastPageInSplitMode?: boolean;
    public orientationListener!: EventListener;
    public PAGE_SCROLLING_DELAY: number = 1000;

    public showThumbnails: boolean = false;

    public showStartDialog: boolean = false;
    public showBookmarkDialog: boolean = false;
    public wasSwipe: boolean = false;

    // -- Pulse Values
    private pulseTimestamp: string | undefined;
    private pulseId: NodeJS.Timeout | undefined;
    private readonly PULSE_INTERVAL: number = 5000;
    private readonly PULSE_INTERVAL_PUFFER: number = 5000;
    private readonly PULSE_INTERVAL_WAKE_AWAIT: number = 5000;

    /**
     * Flag that is used to protect the application from sending a beacon request twice
     */
    private beaconWasSend: boolean = false;
    private metaPressed: boolean = false;

    /**
     * handle zoom in and out through keyinput
     */
    public handlePress(event: any): void {
        if (event.key === 'Meta') {
            this.metaPressed = true;
        }
        // zoom in if meta key and + is pressed
        if (event.key === '+' && this.metaPressed) {
            // prevent the default function -> native browser zoom in
            event.preventDefault();
            this.onZoomClicked('in');
        }
        // zoom out if meta key and + is pressed
        if (event.key === '-' && this.metaPressed) {
            // prevent the default function -> native browser zoom out
            event.preventDefault();
            this.onZoomClicked('out');
        }
    }

    /**
     * handle if meta key, on mac command | on windows control, is pressed
     */
    public handleKeyUp(event: any) {
        if (event.key === 'Meta') {
            this.metaPressed = false;
        }
    }

    public created() {
        // register keypress listener
        window.addEventListener('keydown', this.handlePress);
        window.addEventListener('keyup', this.handleKeyUp);
        this.orientationListener = () => this.checkOrientation();
        this.initTrackingData();
        // set global event listener
        this.setGlobalEventListener();

        if (this.activeBookmark) {
            this.showStartDialog = true;
        }

        this.checkOrientation();

        // show thumbnails after a delay
        setTimeout(() => {
            this.showThumbnails = true;
        }, 1000);
        // start the inactivity timer to monitor inactivity
        this.setInactivityTimer();
        // Starts the tracking pulse
        this.startTrackingPulse();
    }

    /**
     * function is called just before element get destroyed
     */
    public beforeDestroy() {
        this.finishTrackingPulse();
        // remove keypress listener
        window.removeEventListener('keydown', this.handlePress);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    public mounted() {
        const element = document.querySelector('.page-container');
        const container = document.getElementById('reader');
        this.initPinchZoom((element! as HTMLElement), (container! as HTMLElement));
    }

    get showSecondPage(): boolean {
        return this.isSplitMode! && this.currentPage !== 0 &&
            this.currentPage !== this.selectedMagazine.pageCount - 1 && !this.selectedMagazine.showSinglePage;
    }

    /**
     * Inits the tracking process
     */
    public initTrackingData() {
        this.startTracking({
            // if no current Location is defined, the backend add this tracking data automatically to "probelesen location"
            locationId: this.currentLocation?.id ?? 0,
            publicationId: this.selectedMagazine.publicationId,
            magazineId: this.selectedMagazine.id,
            readerId: this.currentUser.id!,
            publisherId: this.selectedMagazine.publisherId
        });
    }

    private setGlobalEventListener() {
        EventBus.$on('BOOKMARK_CLICKED', this.bookmarkClicked);
        window.addEventListener('orientationchange', this.orientationListener);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('unload', this.onUnload);
    }

    private removeGlobalEventListener() {
        EventBus.$off('BOOKMARK_CLICKED', this.bookmarkClicked);
        window.removeEventListener('orientationchange', this.orientationListener);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /**
     * Event listener that handles visibility change
     */
    private onVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            this.beaconWasSend = true;
            this.sendTracking();
            this.clearInactivityTimer();
        } else {
            this.setInactivityTimer();
            this.beaconWasSend = false;
            this.initTrackingData();
            // add current page to tracking
            this.addTrackingPage({
                page: (this.currentPage + 1).toString(),
                // Page 1 is never in split mode!
                splitMode: this.currentPage + 1 === 1 ? false : this.isSplitMode!
            });
        }
    }

    /**
     * Event handler when window is unloading
     */
    private onUnload() {
        if (!this.beaconWasSend) {
            this.beaconWasSend = true;
            this.sendTracking();
            this.clearInactivityTimer();
        }
    }

    public openBookmarkPage() {
        this.showStartDialog = false;
        this.showBookmarkDialog = false;
        this.openThumbnailPage(this.activeBookmark?.page!);
    }

    public removeBookmarkClicked() {
        this.showBookmarkDialog = false;
        this.removeBookmark(this.activeBookmark!);
    }

    public saveCurrentPageAsBookmark() {
        this.showBookmarkDialog = false;
        this.saveBookmark(new Bookmark({
            publicationId: this.selectedMagazine.publicationId,
            page: this.currentPage,
            date: new Date(),
            type: BookmarkType.MANUAL
        }));
    }

    public pageButtonClicked(direction: ScrollDirection) {
        if (this.isZoomedIn) {
            this.scrollButtonClicked(direction);
        } else {
            if (direction === ScrollDirection.LEFT) {
                this.openPrevPage();
            } else if (direction === ScrollDirection.RIGHT) {
                this.openNextPage();
            }
        }
    }

    public onSwipe(event: any) {
        if (this.isZoomedIn) { // The user zoomed in -> don't go to another page
            return;
        }

        if (event.type === 'swipeleft') {
            this.openNextPage();
        } else {
            this.openPrevPage();
        }
        /*
            The browser also triggers the toggleThumbnails event after the swipe
            Disable it by setting the wasSwipe boolean for a short time to true!
         */
        this.wasSwipe = true;
        setTimeout(() => {
            this.wasSwipe = false;
        }, 100);
    }

    public destroyed() {
        // The user has not saved a bookmark -> save an automatic one!
        if ((!this.activeBookmark || this.activeBookmark.type === BookmarkType.AUTOMATIC) && this.currentPage > 0) {
            this.saveBookmark(new Bookmark({
                publicationId: this.selectedMagazine.publicationId,
                page: this.currentPage,
                date: new Date(),
                type: BookmarkType.AUTOMATIC
            }));
        }
        this.sendTracking();
        this.clearTracking();
        // remove global event listener
        this.removeGlobalEventListener();
    }

    public checkOrientation() {
        const orientation = (screen.orientation || {}).type;
        // check if split screen should be showed
        let showSplitScreen = this.$vuetify.breakpoint.mdAndUp &&
            (orientation === 'landscape-primary' || screen.width > screen.height); // Safari does not support screen.orientation...

        // if magazine has singlePageOnly attribute set to true, do not use split screen
        if (this.selectedMagazine.showSinglePage) {
            showSplitScreen = false;
        }

        // The reader was opened the first time. The first page is always in single Mode!
        if (this.isSplitMode === undefined && this.currentPage === 0) {
            this.addTrackingPage({
                page: (this.currentPage + 1).toString(),
                splitMode: false
            });
        } else if (this.isSplitMode !== showSplitScreen) { // The orientation changed -> add tracking page with new splitMode
            if (this.currentPage === 0) { // Page 0 is never in split mode!
                this.addTrackingPage({
                    page: (this.currentPage + 1).toString(),
                    splitMode: false
                });
            } else {
                this.addTrackingPage({
                    page: (this.currentPage + 1).toString(),
                    splitMode: showSplitScreen && this.showLastPageInSplitMode!
                });
            }
        }

        this.isSplitMode = showSplitScreen;
        this.$forceUpdate(); // Necessary to reload the page
    }

    private openPrevPage() {
        if (this.showThumbnails) {
            this.showThumbnails = false; // Hide the thumbnails first
            return;
        }

        if (this.isZoomedIn) { // Ignore the event while the user is in zoomingMode
            return;
        }

        if (this.isSplitMode && this.currentPage !== 1) {
            this.changeReaderPage(this.currentPage - 2);
        } else {
            this.changeReaderPage(this.currentPage - 1);
        }
    }

    private openNextPage() {
        if (this.showThumbnails) {
            this.showThumbnails = false; // Hide the thumbnails first
            return;
        }

        if (this.isZoomedIn) { // Ignore the event while the user is in zoomingMode
            return;
        }

        if (this.isSplitMode && this.currentPage !== 0) {
            this.changeReaderPage(this.currentPage + 2);
        } else {
            this.changeReaderPage(this.currentPage + 1);
        }
    }

    public changeReaderPage(newPage: number) {
        this.setInactivityTimer();
        if (newPage >= this.selectedMagazine.pageCount || newPage < 0) {
            return;
        }

        if (this.currentPage > newPage) {
            this.slideMode = SlideValue.LEFT;
        } else {
            this.slideMode = SlideValue.RIGHT;
        }

        this.currentPage = newPage;
        this.showLastPageInSplitMode = !(this.currentPage % 2 !== 0 && this.currentPage === this.selectedMagazine.pageCount - 1);

        if (newPage === 0) {
            this.addTrackingPage({ page: (this.currentPage + 1).toString(), splitMode: false });
        } else {
            this.addTrackingPage({
                page: (this.currentPage + 1).toString(),
                splitMode: this.isSplitMode! && this.showLastPageInSplitMode
            });
        }
        this.changedPage = !this.changedPage;

        // Reset the values with a small delay. Else the swipe gesture will overwrite everything
        this.resetPosition();
    }

    private openThumbnailPage(page: number) {
        if (this.currentPage === page) {
            return;
        }

        if (this.currentPage > page) {
            this.slideMode = SlideValue.LEFT;
        } else {
            this.slideMode = SlideValue.RIGHT;
        }

        if (this.isSplitMode && page % 2 === 0 && page !== 0) {
            this.changeReaderPage(page - 1);
        } else {
            this.changeReaderPage(page);
        }
    }

    private toggleThumbnails() {
        if (this.isZoomedIn || this.wasSwipe || this.showThumbnails) { // The user zoomed in -> dont open the thumbnails
            this.showThumbnails = false;
            return;
        }
        setTimeout(() => {
            // The doubleTap will be detected after 300ms, so wait for it. Not the best solution.
            if (!this.isZoomedIn) {
                this.showThumbnails = !this.showThumbnails;
            }
        }, 300);
    }

    private bookmarkClicked() {
        if (this.activeBookmark) {
            this.showBookmarkDialog = true;
        } else {
            this.saveCurrentPageAsBookmark();
        }
    }

    /**
     * Starts a tracking pulse to handle edge case like sleep mode
     *
     * This pulse checks every x seconds when the previous pulse were made and sends or
     * updates the tracking data. Data is sent if the previous pulse was saved too long ago
     * (if the computer went to sleep mode for example, none of our event listener is fired then!). And
     * the data is updated if the previous pulse was saved in a acceptable time.
     */
    private startTrackingPulse() {
        this.pulseTimestamp = moment().toISOString();

        this.pulseId = setInterval(async () => {
            const now = moment();

            if (this.pulseTimestamp) {
                const compare = moment(this.pulseTimestamp);
                const diff = now.diff(compare);

                if (diff > this.PULSE_INTERVAL + this.PULSE_INTERVAL_PUFFER) {

                    // The timeout is necessary, because sometime the network request handler
                    // is not immediately ready after getting out of sleep mode. Without this timeout
                    // the tracking request can not be performed.
                    setTimeout(async () => {
                        await this.sendTracking(true);
                        this.initTrackingData();
                        // add current page to tracking
                        this.addTrackingPage({
                            page: (this.currentPage + 1).toString(),
                            // Page 1 is never in split mode!
                            splitMode: this.currentPage + 1 === 1 ? false : this.isSplitMode!
                        });
                        this.setInactivityTimer();
                    }, this.PULSE_INTERVAL_WAKE_AWAIT);
                }
            }
            this.pulseTimestamp = now.toISOString();
        }, this.PULSE_INTERVAL);
    }

    /**
     * Finishes the tracking pulse
     * @private
     */
    private finishTrackingPulse() {
        if (this.pulseId) {
            clearInterval(this.pulseId);
        }
    }
};
